import {
  Container,
  AppHeader,
  AppHeaderBackButton,
  Typography,
  Button,
  ButtonGroup,
} from '@ace-clublabs/shared-component-library';
import sorryPageImg from '../assets/sorry_page.png';
import noMoneyImg from '../assets/no_money.png';
import linkExpiredImg from '../assets/link_expired.png';
import { useLayoutEffect, useState } from 'react';
import { UtagHelper, UtagType } from '../utils/UtagHelper';
import { setGlobalState, useGlobalState } from '../state/state';
import { getEnvironmentFromHost } from '../api/endpoints';
import { clearGlobalState } from '../state/helpers';
import { getIdentityUrl } from '../components/AuthenticationProvider/utils/helpers';

interface ISorryPageProps {
  errorType?: 'payment' | 'timeout';
}

export const SorryPage: React.FC<ISorryPageProps> = (props: ISorryPageProps) => {
  const [tags] = useGlobalState('tags');
  const [isTagging, setTagging] = useState(true);
  const errorType = props?.errorType || '';
  const { onClickBackButton, backButtonLabel } = getBackButtonProps(errorType);
  const loginUrl = getIdentityUrl('login', `${window.location.host}/membership/replacementcard`);

  useLayoutEffect(() => {
    UtagHelper.sendTag(UtagType.view, tags, 'error-general', {
      error: `${errorType === 'payment' ? 'payment failed' : 'tbd error description'}`,
    });
    clearGlobalState(setGlobalState);
    setTagging(false);
  }, []);

  if (isTagging) {
    return <></>;
  }

  return (
    <Container maxWidth="sm">
      <AppHeader
        backButton={
          props?.errorType !== 'timeout' ? (
            <AppHeaderBackButton onClick={onClickBackButton} label={backButtonLabel} id="back-button" />
          ) : undefined
        }
      />

      <img src={getErrorImageSrc(errorType)} alt="technical-difficulties-img" style={{ width: '100%' }} />

      <Typography className="sorry-page-title" variant="h5" sx={{ marginBottom: '1rem' }}>
        {getErrorTitle(errorType)}
      </Typography>
      <Typography className="try-again-label" variant="b2Regular" sx={{ marginBottom: '1rem' }}>
        {getErrorMessage(errorType)}
      </Typography>

      <Typography className="customer-service-hours-label" variant="b2Regular" sx={{ fontWeight: '500' }}>
        Customer service hours
      </Typography>
      <Typography className="weekday-hours-label" variant="b2Regular">
        Monday - Friday: 7 a.m. to 5 p.m.
      </Typography>
      <Typography className="weekend-hours-label" variant="b2Regular" sx={{ marginBottom: '1rem' }}>
        Saturday: 8 a.m. to 5 p.m.
      </Typography>

      <ButtonGroup>
        {getButtonGroup(errorType, loginUrl).map((b: any) => (
          <Button
            id={b.id}
            label={b.label}
            fullWidth
            onClick={b.onClick || undefined}
            variant={b.variant || undefined}
            href={b.href || undefined}
          />
        ))}
      </ButtonGroup>
    </Container>
  );
};

function getBackButtonProps(errorType?: string) {
  switch (errorType) {
    case 'payment': {
      return {
        onClickBackButton: () => window.location.assign(window.location.href.replace('/sorry/payment', '')),
        backButtonLabel: 'Back to my membership',
      };
    }
    default: {
      return {
        onClickBackButton: () => window.location.assign('https://account.app.ace.aaa.com'),
        backButtonLabel: 'Back to my account',
      };
    }
  }
}

function getButtonGroup(errorType?: string, logoutUrl?: string) {
  const env = getEnvironmentFromHost(window.location.hostname);

  const BUTTON_PROPS = {
    customerService: {
      id: 'customer-service-phone-button',
      label: 'Call 1-844-786-1615',
      href: 'tel:+18447861615',
      variant: 'outlined',
    },
    retry: {
      id: 'retry-button',
      label: 'Retry',
      onClick: () => {
        // should this redirect to replacement card instead?
        window.location.assign(`https://${window.location.host}/membership/selfservice`);
      },
    },
    login: {
      id: 'login-button',
      label: 'Log in',
      onClick: () => {
        window.location.assign(getIdentityUrl('login', `https://${window.location.host}/membership/replacementcard`));
      },
    },
  };

  switch (errorType) {
    case 'payment':
      return [BUTTON_PROPS.customerService];
    case 'timeout':
      return [BUTTON_PROPS.login, BUTTON_PROPS.customerService];
    default:
      return [BUTTON_PROPS.retry, BUTTON_PROPS.customerService];
  }
}

function getErrorImageSrc(errorType?: string) {
  switch (errorType) {
    case 'payment':
      return noMoneyImg;
    case 'timeout':
      return linkExpiredImg;
    default: {
      return sorryPageImg;
    }
  }
}

function getErrorTitle(errorType?: string) {
  switch (errorType) {
    case 'payment':
      return 'Sorry, there was a problem processing your payment';
    case 'timeout':
      return 'Your session has timed out';
    default:
      return "We couldn't load your information";
  }
}

function getErrorMessage(errorType?: string) {
  switch (errorType) {
    case 'timeout':
      return "To protect your personal information, we've closed your session due to inactivity.";
    default:
      return 'Please try again later or contact us for assistance.';
  }
}
